import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
export * from "./validateParams.js";
export * from "./CustomError.js";
export * from "./plans.js";
export * from "./hooks/useUploader.js";
export * from "./hooks/useResponsive.js";
export * from "./getDataSummary.js";
export * from "./variableDetection.js";
export * from "./hooks/usePaginationSearch.js";
export * from "./generateCodeDiff.js";
export * from "./useWhyDidYouUpdate.js";
export * from "./workflowUsage.js";
export * from "./sharedFunctionName.js";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export const isNumber = (value) => {
    return typeof value === "number";
};

export const makeId = (length) => {
    const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    const max = 256 - (256 % charactersLength);
    const buf = new Uint8Array(Math.min(Math.max(length * 2, 16), 4096));
    let result = "";
    while (result.length < length) {
        globalThis.crypto.getRandomValues(buf);
        for (let i = 0; i < buf.length && result.length < length; i++) {
            if (buf[i] < max) {
                result += characters.charAt(buf[i] % charactersLength);
            }
        }
    }
    return result;
};

// example: date to 5 minutes ago
export function formatDate(date) {
    const diff = new Date() - date;
    const sec = Math.floor(diff / 1000);
    const min = Math.floor(sec / 60);
    const hour = Math.floor(min / 60);
    const day = Math.floor(hour / 24);

    if (day > 1) {
        return `${day} days ago`;
    } else if (hour > 1) {
        return `${hour} hours ago`;
    } else if (min > 1) {
        return `${min} minutes ago`;
    }

    return `${sec} seconds ago`;
}

export function isCSV(str) {
    // Basic checks
    if (!str || typeof str !== "string") {
        return false;
    }

    // Remove outer quotes if they exist
    const content =
        str.trim().startsWith('"') && str.trim().endsWith('"')
            ? str.trim().substring(1, str.trim().length - 1)
            : str;

    // Look for CSV indicators
    const hasNewlineDelimiters = content.includes("\n");
    const hasCommas = content.includes(",");
    const hasConsistentStructure = (() => {
        // Split by newlines and check if fields are consistent
        const lines = content.split("\\n");
        if (lines.length < 2) return false;

        // Try to count fields in first line (accounting for quotes)
        let inQuotes = false;
        let fieldCount = 1; // Start with 1 for the first field

        for (let i = 0; i < lines[0].length; i++) {
            const char = lines[0][i];

            // Handle quote state
            if (char === '"') {
                const escaped = i > 0 && lines[0][i - 1] === "\\";
                if (!escaped) inQuotes = !inQuotes;
            }

            // Count commas outside of quotes
            if (char === "," && !inQuotes) {
                fieldCount++;
            }
        }

        // Check if subsequent lines have similar field counts
        // (just check the second line for efficiency)
        if (lines.length > 1) {
            let secondLineFields = 1;
            inQuotes = false;

            for (let i = 0; i < lines[1].length; i++) {
                const char = lines[1][i];

                if (char === '"') {
                    const escaped = i > 0 && lines[1][i - 1] === "\\";
                    if (!escaped) inQuotes = !inQuotes;
                }

                if (char === "," && !inQuotes) {
                    secondLineFields++;
                }
            }

            // Allow some flexibility (±1 field) as CSV can sometimes have inconsistent fields
            return Math.abs(fieldCount - secondLineFields) <= 1;
        }

        return fieldCount > 1; // At minimum, CSV should have multiple fields
    })();

    // Check JSON indicators
    const jsonIndicators = [
        // Standard JSON starting characters
        content.trim().startsWith("{"),
        content.trim().startsWith("["),
        // Look for JSON field patterns
        /"[^"]+"\s*:/i.test(content),
        // Check for balanced JSON brackets (simple check)
        (content.match(/{/g) || []).length ===
            (content.match(/}/g) || []).length &&
            content.includes("{") &&
            content.includes("}"),
    ];

    // Determine if it looks like JSON
    const looksLikeJSON = jsonIndicators.some((indicator) => indicator);

    // Final decision based on combined factors
    return (
        hasCommas &&
        hasNewlineDelimiters &&
        hasConsistentStructure &&
        !looksLikeJSON
    );
}

export function isJSON(str) {
    // Quick initial checks
    if (!str || typeof str !== "string") {
        return false;
    }

    // Trim whitespace
    const trimmed = str.trim();

    // JSON must start with { or [
    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
        // Look for JSON wrapped in quotes (stringified JSON)
        if (
            (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"))
        ) {
            // Try to check content within quotes
            const innerContent = trimmed.substring(1, trimmed.length - 1);
            const unescaped = innerContent
                .replace(/\\"/g, '"')
                .replace(/\\'/g, "'");

            if (
                unescaped.trim().startsWith("{") ||
                unescaped.trim().startsWith("[")
            ) {
                // Additional check for key-value patterns
                return /"\s*:\s*["{[\w]/.test(unescaped);
            }
        }
        return false;
    }

    // Check for typical JSON patterns
    const hasKeyValuePair = /"[^"]+"\s*:\s*["{[\w]/.test(trimmed);

    // Quick bracket balance check
    const openCurly = (trimmed.match(/{/g) || []).length;
    const closeCurly = (trimmed.match(/}/g) || []).length;
    const openSquare = (trimmed.match(/\[/g) || []).length;
    const closeSquare = (trimmed.match(/\]/g) || []).length;

    const bracketsBalanced =
        openCurly === closeCurly && openSquare === closeSquare;

    // Check for JSON syntax elements while avoiding full parsing
    // (which would be slower for large strings)
    const hasJsonSyntax =
        /"[^"]*"\s*:/g.test(trimmed) || // Standard key:value
        /:\s*"[^"]*"/g.test(trimmed) || // value assignment
        /:\s*\d+[,}]/g.test(trimmed) || // numeric values
        /:\s*true|false|null[,}]/g.test(trimmed); // booleans/null

    // Combined checks
    const isLikelyJSON = hasKeyValuePair && bracketsBalanced && hasJsonSyntax;

    // If we're very confident, return true without parsing
    if (
        isLikelyJSON &&
        ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
            (trimmed.startsWith("[") && trimmed.endsWith("]")))
    ) {
        return true;
    }

    // Final validation with try/parse for edge cases
    // Only do this as last resort for performance
    try {
        JSON.parse(trimmed);
        return true;
    } catch (e) {
        // One last check for stringified JSON
        if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
            try {
                // Check if it's JSON wrapped in a string
                const parsed = JSON.parse(trimmed);
                return typeof parsed === "string" && isJSON(parsed);
            } catch (e) {
                return false;
            }
        }
        return false;
    }
}

export const flattenObject = (obj, prefix = "") => {
    // Handle null or undefined
    if (obj === null || obj === undefined) {
        return {};
    }

    // Handle primitive values
    if (typeof obj !== "object") {
        return { [prefix]: obj };
    }

    // Handle arrays specially
    if (Array.isArray(obj)) {
        return { [prefix]: JSON.stringify(obj) };
    }

    // For empty objects
    if (Object.keys(obj).length === 0) {
        return { [prefix]: "" };
    }

    return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (obj[key] === null || obj[key] === undefined) {
            acc[newKey] = "";
        } else if (typeof obj[key] === "object") {
            if (Array.isArray(obj[key])) {
                // Handle arrays as strings
                acc[newKey] = JSON.stringify(obj[key]);
            } else {
                // Recursively flatten nested objects
                const flattenedChild = flattenObject(obj[key], newKey);
                Object.assign(acc, flattenedChild);
            }
        } else {
            // Handle primitive values
            acc[newKey] = obj[key];
        }

        return acc;
    }, {});
};

export function parseStyleString(styleString) {
    if (!styleString) return {};

    return styleString
        .split(";")
        .filter((style) => style.trim())
        .reduce((acc, style) => {
            const [property, value] = style.split(":").map((str) => str.trim());
            acc[property] = value;
            return acc;
        }, {});
}

export const pageSizeStyles = {
    A4: {
        width: "8.27in",
        height: "11.69in",

        padding: 0, // Remove padding here since it's handled in document-editor-content
        boxSizing: "border-box",
        position: "relative",
        backgroundColor: "white",
    },
    "US-Legal": {
        width: "8.5in",
        height: "14in",
        padding: 0, // Remove padding here since it's handled in document-editor-content
        boxSizing: "border-box",
        position: "relative",
        backgroundColor: "white",
    },
    "US-Letter": {
        width: "8.5in",
        height: "11in",
        padding: 0, // Remove padding here since it's handled in document-editor-content
        boxSizing: "border-box",
        position: "relative",
        backgroundColor: "white",
    },
    "US-Tabloid": {
        width: "11in",
        height: "17in",
        padding: 0, // Remove padding here since it's handled in document-editor-content
        boxSizing: "border-box",
        position: "relative",
        backgroundColor: "white",
    },
};

export const promisify = (fn) => {
    return (...args) => {
        return new Promise((resolve, reject) => {
            fn(...args, (err, result) => (err ? reject(err) : resolve(result)));
        });
    };
};

export function isEmailValid(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function extractDatabaseAndTable(str) {
    // Initialize with null values in case either is not found
    let databaseName = null;
    let tableName = null;

    // Try to match the explicit @[Database: ... Table: ...] format first
    const explicitMatch = str.match(
        /@\[Database:\s*([\w-]+)\s*Table:\s*(\S+)\]/
    );
    if (explicitMatch) {
        databaseName = explicitMatch[1];
        tableName = explicitMatch[2];
        return { databaseName, tableName };
    }

    // Try to match simple "Database: ... Table: ..." format
    const simpleMatch = str.match(/Database:\s*([\w-]+)\s*Table:\s*(\S+)/);
    if (simpleMatch) {
        databaseName = simpleMatch[1];
        tableName = simpleMatch[2];
        return { databaseName, tableName };
    }

    // If no explicit format, try to find database name from various phrases
    const dbPhraseMatch = str.match(
        /(?:base de datos|database)\s+['"]?([\w-]+)['"]?/i
    );
    if (dbPhraseMatch) {
        databaseName = dbPhraseMatch[1];
    }

    // Try to find table name from various phrases
    const tablePhraseMatch = str.match(/(?:tabla|table)\s+['"]?([\w-]+)['"]?/i);
    if (tablePhraseMatch) {
        tableName = tablePhraseMatch[1];
    }

    return { databaseName, tableName };
}

export const extractIntegrations = (text) => {
    const regex = /@\[(.*?)\]/g;
    const matches = [...text.matchAll(regex)];
    return matches.map((match) => match[1]);
};

export const cleanFileName = (fileName) => {
    // Split filename into name and extension
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : "";
    const nameWithoutExt =
        lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;

    // Remove accents/diacritics
    let cleaned = nameWithoutExt
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    // Replace spaces with underscores
    cleaned = cleaned.replace(/\s+/g, "_");

    // Remove special characters except dots, underscores, and hyphens
    cleaned = cleaned.replace(/[^a-zA-Z0-9._-]/g, "");

    // Remove multiple consecutive dots/underscores/hyphens
    cleaned = cleaned.replace(/[._-]+/g, "_");

    // Remove leading/trailing dots, underscores, and hyphens
    cleaned = cleaned.replace(/^[._-]+|[._-]+$/g, "");

    // Convert to lowercase for consistency
    cleaned = cleaned.toLowerCase();

    // Ensure the filename isn't empty
    if (!cleaned) {
        cleaned = "file_" + makeId(5);
    }

    // Reattach the extension
    return cleaned + extension.toLowerCase();
};

export function calculateSimilarity(str1, str2) {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    if (s1 === s2) return 1;
    if (s1.length === 0) return 0;
    if (s2.length === 0) return 0;
    const matrix = Array(s1.length + 1)
        .fill()
        .map(() => Array(s2.length + 1).fill(0));
    for (let i = 0; i <= s1.length; i++) matrix[i][0] = i;
    for (let j = 0; j <= s2.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= s1.length; i++) {
        for (let j = 1; j <= s2.length; j++) {
            const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j - 1] + cost
            );
        }
    }
    const maxLength = Math.max(s1.length, s2.length);
    return 1 - matrix[s1.length][s2.length] / maxLength;
}

// Format phone number to be more user-friendly (same as in WhatsAppTriggerModal)
export const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/[^\d+]/g, "");
    const withPlus = cleaned.startsWith("+") ? cleaned : `+${cleaned}`;

    if (withPlus.length >= 12 && withPlus.startsWith("+1")) {
        const match = withPlus.match(/^\+1(\d{3})(\d{3})(\d{4})(.*)$/);
        if (match) {
            return `+1 (${match[1]}) ${match[2]}-${match[3]}${
                match[4] ? ` ${match[4]}` : ""
            }`;
        }
    } else if (withPlus.length >= 10) {
        const countryCode = withPlus.substring(1, withPlus.length - 9);
        const remaining = withPlus.substring(withPlus.length - 9);
        const match = remaining.match(/^(\d{3})(\d{3})(\d{3})(.*)$/);
        if (match) {
            return `+${countryCode} ${match[1]} ${match[2]} ${match[3]}${
                match[4] ? ` ${match[4]}` : ""
            }`;
        }
    }

    return withPlus.replace(/(\+\d{1,3})(\d{3})(\d{3})(\d+)/, "$1 $2 $3 $4");
};
